// src/pages/MembersPage.jsx
import React, { useEffect, useState } from 'react';
import './MembersPage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUp,
  faArrowDown,
  faUserSlash,
  faUserCheck,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';

const MembersPage = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('userId');

  const fetchMembers = async () => {
    try {
      const res = await fetch('https://ccjab.pro/api/users.php/all');
      const data = await res.json();

      if (Array.isArray(data)) {
        const filtered = data.filter(member => String(member.id) !== currentUserId);
        setMembers(filtered);
        setFilteredMembers(filtered);
      } else {
        setMembers([]);
        setFilteredMembers([]);
      }
    } catch (err) {
      console.error('Erreur récupération des membres:', err);
      setMembers([]);
      setFilteredMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [currentUserId]);

  useEffect(() => {
    const q = searchQuery.toLowerCase();
    const filtered = members.filter(member =>
      member.username.toLowerCase().includes(q) || member.email.toLowerCase().includes(q)
    );
    setFilteredMembers(filtered);
  }, [searchQuery, members]);

  const handleToggleRole = async (user) => {
    if (user.role === 'admin') return;

    const action = user.role === 'user' ? 'promouvoir en agent' : 'redescendre à user';
    const icon = user.role === 'user' ? 'info' : 'question';

    const confirm = await Swal.fire({
      title: `Confirmer ?`,
      text: `Souhaitez-vous ${action} ?`,
      icon: icon,
      showCancelButton: true,
      confirmButtonColor: '#009688',
      cancelButtonText: 'Annuler',
      confirmButtonText: 'Oui, confirmer'
    });

    if (confirm.isConfirmed) {
      try {
        const res = await fetch(`https://ccjab.pro/api/users.php/${user.id}/toggle-role`, {
          method: 'PUT'
        });
        const result = await res.json();

        if (res.ok) {
          toast.success(`Rôle changé en ${result.newRole}`);
          fetchMembers();
        } else {
          toast.error(result.message || 'Erreur de changement de rôle');
        }
      } catch (err) {
        toast.error('Erreur réseau');
      }
    }
  };

  const handleDisable = async (userId) => {
    const confirm = await Swal.fire({
      title: 'Confirmer la désactivation ?',
      text: 'Le membre sera désactivé.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Oui, désactiver',
      cancelButtonText: 'Annuler'
    });

    if (confirm.isConfirmed) {
      try {
        const res = await fetch(`https://ccjab.pro/api/users.php/${userId}/disable`, { method: 'PUT' });
        const result = await res.json();
        if (res.ok) {
          toast.success('Compte désactivé');
          fetchMembers();
        } else {
          toast.error(result.message || 'Erreur désactivation');
        }
      } catch (err) {
        toast.error('Erreur réseau');
      }
    }
  };

  const handleReactivate = async (userId) => {
    const confirm = await Swal.fire({
      title: 'Réactiver le compte ?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Oui, réactiver',
      cancelButtonText: 'Annuler'
    });

    if (confirm.isConfirmed) {
      try {
        const res = await fetch(`https://ccjab.pro/api/users.php/${userId}/reactivate`, { method: 'PUT' });
        const result = await res.json();
        if (res.ok) {
          toast.success('Compte réactivé');
          fetchMembers();
        } else {
          toast.error(result.message || 'Erreur réactivation');
        }
      } catch (err) {
        toast.error('Erreur réseau');
      }
    }
  };

  const handleViewActivity = (userId) => {
    navigate(`/activity/${userId}`);
  };

  return (
    <div className="members-page">
      <h2 className="title">Liste des membres</h2>

      <div className="search-bar-wrapper">
        <input
          type="text"
          placeholder="Rechercher par nom ou email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <div className="members-grid">
          {filteredMembers.map((member) => (
            <div className={`member-card ${member.active === 0 ? 'disabled' : ''}`} key={member.id}>
              <img
                src={member.profile_photo || 'https://via.placeholder.com/90'}
                alt="Profil"
                className="member-photo"
              />
              <div className="member-info">
                <h4>{member.username}</h4>
                <p>{member.email}</p>
                <span className="member-role">{member.role}</span>
                {member.active === 0 && <div className="member-status">Compte désactivé</div>}
              </div>

              <div className="member-actions">
                <button
                  className="icon-button"
                  onClick={() => handleToggleRole(member)}
                  title={member.role === 'user' ? 'Promouvoir en agent' : 'Redescendre à user'}
                  disabled={member.active === 0 || member.role === 'admin'}
                >
                  <FontAwesomeIcon icon={member.role === 'user' ? faArrowUp : faArrowDown} />
                </button>

                {member.active === 1 ? (
                  <button
                    className="icon-button"
                    onClick={() => handleDisable(member.id)}
                    title="Désactiver"
                  >
                    <FontAwesomeIcon icon={faUserSlash} />
                  </button>
                ) : (
                  <button
                    className="icon-button"
                    onClick={() => handleReactivate(member.id)}
                    title="Réactiver"
                  >
                    <FontAwesomeIcon icon={faUserCheck} />
                  </button>
                )}

                <button
                  className="icon-button"
                  onClick={() => handleViewActivity(member.id)}
                  title="Voir activité"
                  disabled={member.active === 0}
                >
                  <FontAwesomeIcon icon={faChartLine} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MembersPage;